/**
 * @file 本 (ISBN) が載っているカリキュラムの名前を作る。
 *
 * @what: 家族が取り込んだ参考書ルートを1冊ずつ見て、本 (ISBN) → カリキュラムの短い名前 の表を作る。
 *   短い名前は、対象の名前 (route.target) の括弧より前を取る。「中学入試（桜蔭・女子学院・雙葉）」は
 *   「中学入試」になり、括弧の無い「気象予報士試験」はそのまま残る。
 * @why: 本だなのカードに、その本がどのカリキュラムのものかを出す (代表の要望、2026-09-16)。対象の名前
 *   をそのまま出すと括弧の中の志望校や注記でカードの1行を使い切るので、括弧の前で切る。同じ対象に
 *   複数の系列があるとき (大学入試の理系編と文系編) は短い名前が同じになるので、1つにまとめる。
 * @failure: 副作用を持たない純関数である。ISBN を持たない本は表に入らないので、ISBN の無い資料には
 *   タグが付かない。取り込んだ計画かどうかは見ない (家族にあるカリキュラム全部を対象にする)。
 */

import { latestTextbookRoutes, type StudyTextbookRoute } from "../api/textbookRoutes";

/** 対象の名前から、タグに出す短い名前を取る (括弧より前を取り、括弧で始まる名前はそのまま返す)。 */
export function shortCurriculumName(target: string): string {
  const at = target.search(/[（(]/);
  const head = at > 0 ? target.slice(0, at) : target;
  const trimmed = head.trim();
  return trimmed.length > 0 ? trimmed : target.trim();
}

/**
 * 本 (ISBN) ごとに、その本が載っているカリキュラムの短い名前を集める。
 *
 * @what: 最新の改訂版のルートだけを見る。同じ本が同じ名前で2度出ても1つにする。並びはルートを
 *   受け取った順である。
 */
export function curriculumNamesByIsbn(
  routes: readonly StudyTextbookRoute[],
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const route of latestTextbookRoutes(routes)) {
    const name = shortCurriculumName(route.target);
    for (const subject of route.subjects) {
      for (const track of subject.tracks) {
        for (const book of track.books) {
          const isbn = book.isbn;
          if (!isbn) continue;
          const names = out.get(isbn);
          if (names === undefined) out.set(isbn, [name]);
          else if (!names.includes(name)) names.push(name);
        }
      }
    }
  }
  return out;
}
